// One build-step row (Flow 5; docs/phase-0/slice5-ux-flows.md §8.2-8.3;
// docs/phase-0/slice5-component-designs.md §3.5 `.build-step-row`) - the fourth reuse of
// the `.control-row` rhythm in this slice. Reads ONLY `BuildStepView` (id, kind, label,
// state) - that projection is `z.strictObject` and structurally OMITS `runnerClass` and
// any cost figure (packages/shared/src/model/build.ts:122-134, D27/§0#6): this row
// cannot render either even by accident, since the type carries neither. NOTE:
// `awaiting_hard_gate` is a `BuildResult`-LEVEL state only (model/build.ts:147), never a
// per-step `BuildStepState` value (:111-119 has no such member) - the hard gate always
// pauses the WHOLE build, so it is handled one level up by the screen, never per-row.
import type { BuildStepState, BuildStepView } from "@nightcrew/shared";
import { buildCopy, type BuildCopyKey } from "../copy/build.js";

const STATE_CLASS: Record<BuildStepState, string> = {
  pending: "status-chip neutral",
  running: "status-chip neutral",
  done: "status-chip good",
  failed: "status-chip risk",
  awaiting_manual_step: "status-chip warn",
  verifying: "status-chip warn",
};

const STATE_LABEL_KEY: Record<BuildStepState, BuildCopyKey> = {
  pending: "build.mvp.step.state.pending",
  running: "build.mvp.step.state.running",
  done: "build.mvp.step.state.done",
  failed: "build.mvp.step.state.failed",
  awaiting_manual_step: "build.mvp.step.state.awaitingManualStep",
  verifying: "build.mvp.step.state.verifying",
};

// Only `awaiting_manual_step` is a per-STEP held state; `awaiting_hard_gate` is a BuildResult-level
// state handled by the build screen, not a step row.
const HELD_STATES: ReadonlySet<BuildStepState> = new Set<BuildStepState>(["awaiting_manual_step"]);

export const BuildStepRow = ({
  step,
  onRetry,
}: {
  readonly step: BuildStepView;
  readonly onRetry?: () => void;
}): React.JSX.Element => {
  const held = HELD_STATES.has(step.state);
  return (
    <li
      className="build-step-row"
      data-testid="build-step-row"
      data-state={step.state}
      style={held ? { borderInlineStart: "3px solid var(--warn)", paddingInlineStart: 10 } : undefined}
    >
      <div className="row aic between gap12">
        <span>
          {step.label}
          {step.state === "running" ? (
            <span className="thinking-dots" aria-hidden="true" style={{ marginInlineStart: 8 }}>
              <span />
              <span />
              <span />
            </span>
          ) : null}
        </span>
        <span className={STATE_CLASS[step.state]}>{buildCopy(STATE_LABEL_KEY[step.state])}</span>
      </div>
      {step.state === "failed" && onRetry !== undefined ? (
        <button type="button" className="btn btn-ghost btn-sm mt8" onClick={onRetry}>
          {buildCopy("build.mvp.step.retryCta")}
        </button>
      ) : null}
    </li>
  );
};
